import { useCallback, useEffect, useMemo, useState } from 'react';
import { autoCompleteSettlements } from '../feature';
import { useAppDispatch } from '@/store';
import debounce from 'lodash.debounce';
import { ISettlement } from '@/models/regions.model';
import { normalizeApostrophes } from '@/utils';

type TUseAutocompleteSettlementsOptions = {
  withPickup?: boolean;
};

export const useAutocompleteSettlements = (
  defaultSettlementName = '',
  options?: TUseAutocompleteSettlementsOptions,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [settlements, setSettlements] = useState<ISettlement[]>([]);
  const dispatch = useAppDispatch();
  const withPickup = options?.withPickup ?? false;

  const handleSearch = useCallback(
    debounce(async (search: string, regionId?: string, limit = 50) => {
      try {
        setIsLoading(true);
        const response = await dispatch(
          autoCompleteSettlements({
            search: normalizeApostrophes(search),
            region_id: regionId,
            limit,
            with_region: withPickup ? '0' : '1',
            ...(withPickup ? { with_pickup: '1' as const } : {}),
          }),
        ).unwrap();

        setSettlements(response.data ?? []);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [dispatch, withPickup],
  );

  useEffect(() => {
    setSettlements([]);
    handleSearch(defaultSettlementName);
  }, [handleSearch, defaultSettlementName]);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  return useMemo(
    () => ({
      settlements: settlements ?? [],
      settlementsOptions: settlements?.map((item) => ({
        label: item.name ?? '',
        value: item.id,
        regionId: item.region_id,
        latitude: item.latitude,
        longitude: item.longitude,
      })),
      isLoading,
      onSearch: handleSearch,
    }),
    [settlements, isLoading, handleSearch],
  );
};
